import { useEffect, useState, useCallback } from "react";
import {
  Scales, Plus, Sparkle, ShieldCheck, BookOpen, MapPin, CurrencyDollar, Check,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Shared";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const URGENCIES = ["standard", "urgent", "emergency"];
const LINKS = ["none", "shipment", "purchase_order", "job", "listing", "policy", "loan"];

const emptyMatter = {
  title: "", area: "employment", country: "US", description: "", urgency: "standard",
  link_kind: "none", link_ref: "", advisor_id: "",
};
const emptyProfile = {
  firm: "", countries: "", city: "", practice_areas: [], licence_no: "", authority: "",
  languages: "", years_qualified: 0, hourly_rate: "", currency: "usd",
  accepts_fixed_fee: true, remote_ok: true, bio: "", phone: "",
};
const emptyGuide = { title: "", area: "employment", country: "US", summary: "", body: "", published: true };

export default function LegalAdvisory() {
  const { user } = useAuth();
  const isAdvisor = user?.role === "legal_advisor";
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("advisors");
  const [areas, setAreas] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [pending, setPending] = useState([]);
  const [matters, setMatters] = useState({ mine: [], assigned: [], available: [] });
  const [guides, setGuides] = useState([]);
  const [profile, setProfile] = useState(null);
  const [country, setCountry] = useState("");
  const [areaFilter, setAreaFilter] = useState("all");
  const [dialog, setDialog] = useState(null);
  const [matterForm, setMatterForm] = useState(emptyMatter);
  const [profileDraft, setProfileDraft] = useState(emptyProfile);
  const [guideForm, setGuideForm] = useState(emptyGuide);
  const [reply, setReply] = useState({ matter: null, body: "", next_steps: "", ai_assisted: false });
  const [quote, setQuote] = useState({ matter: null, amount: "", currency: "usd", scope: "", turnaround_days: 5 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, m, g, p] = await Promise.all([
        api.get(`/legal/advisors?${country ? `country=${country}&` : ""}${areaFilter !== "all" ? `area=${areaFilter}` : ""}`),
        api.get("/legal/matters"),
        api.get(isAdvisor ? "/legal/guides?mine=true" : "/legal/guides"),
        isAdvisor || isAdmin ? api.get("/legal/profile") : Promise.resolve({ data: null }),
      ]);
      setAdvisors(a.data); setMatters(m.data); setGuides(g.data); setProfile(p.data);
      if (isAdmin) {
        const { data } = await api.get("/legal/advisors?pending=true");
        setPending(data);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [country, areaFilter, isAdvisor, isAdmin]);

  useEffect(() => { api.get("/legal/areas").then(({ data }) => setAreas(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      await load();
      return true;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const raiseMatter = () => run(async () => {
    await api.post("/legal/matters", {
      ...matterForm,
      link_ref: matterForm.link_ref || null,
      advisor_id: matterForm.advisor_id || null,
    });
    setDialog(null);
    setMatterForm(emptyMatter);
  }, "Matter raised");

  const saveProfile = () => run(async () => {
    await api.put("/legal/profile", {
      ...profileDraft,
      countries: String(profileDraft.countries).split(",").map((c) => c.trim()).filter(Boolean),
      languages: String(profileDraft.languages).split(",").map((c) => c.trim()).filter(Boolean),
      years_qualified: Number(profileDraft.years_qualified) || 0,
      hourly_rate: Number(profileDraft.hourly_rate) || 0,
    });
    setDialog(null);
  }, "Practice profile saved");

  const aiDraft = (matter) => run(async () => {
    const { data } = await api.post(`/legal/matters/${matter.id}/ai-draft`);
    setReply({ matter, body: `${data.draft}\n\n— ${data.disclaimer}`, next_steps: "", ai_assisted: true });
    setDialog("reply");
  });

  const sendReply = () => run(async () => {
    await api.post(`/legal/matters/${reply.matter.id}/responses`, {
      body: reply.body, next_steps: reply.next_steps, ai_assisted: reply.ai_assisted,
    });
    setDialog(null);
    setReply({ matter: null, body: "", next_steps: "", ai_assisted: false });
  }, "Advice sent");

  const sendQuote = () => run(async () => {
    await api.post(`/legal/matters/${quote.matter.id}/quotes`, {
      amount: Number(quote.amount) || 0, currency: quote.currency,
      scope: quote.scope, turnaround_days: Number(quote.turnaround_days) || 5,
    });
    setDialog(null);
    setQuote({ matter: null, amount: "", currency: "usd", scope: "", turnaround_days: 5 });
  }, "Fixed fee sent");

  const payQuote = async (q) => {
    try {
      const { data } = await api.post(`/legal/quotes/${q.id}/accept`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const publishGuide = () => run(async () => {
    await api.post("/legal/guides", guideForm);
    setDialog(null);
    setGuideForm(emptyGuide);
  }, "Guide published");

  const areaLabel = (key) => areas.find((a) => a.key === key)?.label || key;

  const TABS = [
    ["advisors", `Advocates (${advisors.length})`, Scales],
    ["matters", `My matters (${matters.mine.length})`, Check],
    ...(isAdvisor || isAdmin ? [["desk", `Advocate desk (${matters.assigned.length + matters.available.length})`, ShieldCheck]] : []),
    ["guides", `Guides (${guides.length})`, BookOpen],
    ...(isAdmin ? [["verify", `Verify (${pending.length})`, ShieldCheck]] : []),
  ];

  return (
    <div data-testid="legal-page">
      <PageHeader
        overline="Legal"
        title="Legal advocates & advisory"
        description="Verified advocates country by country — employment and apprenticeships, customs and trade compliance, contracts, debt recovery and claims. Raise a matter, get written advice, take a fixed fee."
        action={
          <div className="flex flex-wrap gap-2">
            {(isAdvisor || isAdmin) && (
              <Button variant="outline" data-testid="edit-practice-button" onClick={() => {
                setProfileDraft(profile ? {
                  ...profile,
                  countries: (profile.countries || []).join(", "),
                  languages: (profile.languages || []).join(", "),
                } : emptyProfile);
                setDialog("practice");
              }}>
                My practice
              </Button>
            )}
            <Button data-testid="raise-matter-button" onClick={() => { setMatterForm(emptyMatter); setDialog("matter"); }}>
              <Plus size={16} className="mr-1.5" /> Raise a matter
            </Button>
          </div>
        }
      />

      {isAdvisor && profile && !profile.verified && (
        <div data-testid="advisor-pending-banner" className="mb-4 border border-[#F6DCA0] bg-[#FFF8E6] px-4 py-3 text-sm text-[#8A6A00]">
          Your practice is waiting for admin verification. You cannot claim matters or publish guides until then.
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mb-4 min-w-0">
        <TabsList className="tab-strip no-scrollbar rounded-none bg-slate-100">
          {TABS.map(([value, label, Icon]) => (
            <TabsTrigger key={value} value={value} data-testid={`legal-tab-${value}`} className="whitespace-nowrap rounded-none text-xs uppercase tracking-wider">
              <Icon size={14} className="mr-1.5" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {tab === "advisors" && (
        <div data-testid="advisors-panel">
          <div className="mb-4 grid gap-2 sm:grid-cols-[200px_1fr]">
            <Input data-testid="advisor-country-filter" placeholder="Country (US, IN, KE…)" value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} />
            <Select value={areaFilter} onValueChange={setAreaFilter}>
              <SelectTrigger data-testid="advisor-area-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Every practice area</SelectItem>
                {areas.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {advisors.length === 0 ? (
            <EmptyState testid="no-advisors" title="No verified advocates yet" description="Advocates register, an admin verifies their licence, and they appear here by country and practice area." />
          ) : (
            <div className="grid gap-3 lg:grid-cols-2" data-testid="advisor-list">
              {advisors.map((a) => (
                <div key={a.id} data-testid={`advisor-${a.id}`} className="border border-border bg-white p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-2.5">
                      <Avatar imageId={a.logo_id || a.avatar_id} name={a.firm || a.name} size="md" testid={`advisor-avatar-${a.id}`} />
                      <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-sm font-semibold text-[#0F172A]">
                        {a.firm}
                        <ShieldCheck size={14} className="text-[#0B7A5B]" />
                      </div>
                      <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                        <span>{a.name}</span>
                        <span className="inline-flex items-center gap-1"><MapPin size={12} />{[a.city, ...(a.countries || [])].filter(Boolean).join(" · ")}</span>
                        {a.years_qualified ? <span>{a.years_qualified} yrs qualified</span> : null}
                      </div>
                      </div>
                    </div>
                    {a.hourly_rate ? (
                      <span className="text-xs font-semibold text-[#002FA7]">
                        {(a.currency || "usd").toUpperCase()} {Number(a.hourly_rate).toLocaleString()}/hr
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(a.practice_areas || []).map((k) => (
                      <span key={k} className="border border-border px-1.5 py-0.5 text-[0.65rem] uppercase tracking-wider text-slate-600">{areaLabel(k)}</span>
                    ))}
                  </div>
                  {a.bio && <p className="mt-2 line-clamp-3 text-xs text-slate-600">{a.bio}</p>}
                  <Button size="sm" className="mt-3" data-testid={`instruct-${a.id}`} onClick={() => {
                    setMatterForm({ ...emptyMatter, advisor_id: a.id, country: (a.countries || ["US"])[0], area: (a.practice_areas || ["employment"])[0] });
                    setDialog("matter");
                  }}>
                    Instruct this advocate
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "matters" && (
        <div data-testid="matters-panel">
          {matters.mine.length === 0 ? (
            <EmptyState testid="no-matters" title="No matters raised" description="Raise a matter and a verified advocate admitted in that country picks it up." />
          ) : (
            <div className="space-y-3">
              {matters.mine.map((m) => (
                <div key={m.id} data-testid={`matter-${m.id}`} className="border border-border bg-white p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#0F172A]">{m.title}</div>
                      <div className="text-xs text-slate-500">
                        {m.reference} · {m.area_label} · {m.country} · {m.advisor_name || "waiting for an advocate"}
                      </div>
                    </div>
                    <StatusBadge value={m.status} testid={`matter-status-${m.id}`} />
                  </div>
                  {(m.responses || []).map((r) => (
                    <div key={r.id} data-testid={`response-${r.id}`} className="mt-2 whitespace-pre-line border border-[#CBD9FF] bg-[#EEF3FF] p-3 text-xs text-[#0F172A]">
                      <div className="mb-1 font-semibold">{r.advisor_name}{r.ai_assisted ? " · AI-assisted, advocate confirmed" : ""}</div>
                      {r.body}
                      {r.next_steps && <div className="mt-2 font-semibold">Next steps: {r.next_steps}</div>}
                    </div>
                  ))}
                  {(m.quotes || []).map((q) => (
                    <div key={q.id} data-testid={`quote-${q.id}`} className="mt-2 flex flex-wrap items-center justify-between gap-2 border border-border p-3 text-xs">
                      <span>
                        <CurrencyDollar size={12} className="mr-1 inline" />
                        {(q.currency || "usd").toUpperCase()} {Number(q.amount).toLocaleString()} — {q.scope} ({q.turnaround_days} days)
                      </span>
                      {q.payment_status === "paid" ? (
                        <span className="font-semibold text-[#0B7A5B]">Paid</span>
                      ) : q.status === "accepted" ? (
                        <span data-testid={`awaiting-payment-${q.id}`} className="font-semibold text-[#8A6A00]">Awaiting payment</span>
                      ) : (
                        <Button size="sm" data-testid={`pay-quote-${q.id}`} onClick={() => payQuote(q)}>Accept &amp; pay</Button>
                      )}
                    </div>
                  ))}
                  {m.status !== "closed" && (
                    <Button size="sm" variant="ghost" className="mt-2" data-testid={`close-matter-${m.id}`} onClick={() => run(() => api.post(`/legal/matters/${m.id}/close`), "Matter closed")}>
                      Close matter
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "desk" && (isAdvisor || isAdmin) && (
        <div data-testid="desk-panel" className="space-y-6">
          <div>
            <div className="overline mb-2 text-slate-500">Open in my countries and practice areas</div>
            {matters.available.length === 0 ? (
              <EmptyState testid="no-available" title="Nothing waiting" description="New matters in your jurisdictions appear here." />
            ) : (
              <div className="space-y-3">
                {matters.available.map((m) => (
                  <div key={m.id} data-testid={`available-${m.id}`} className="flex flex-wrap items-center justify-between gap-2 border border-border bg-white p-4">
                    <div>
                      <div className="text-sm font-semibold text-[#0F172A]">{m.title}</div>
                      <div className="text-xs text-slate-500">{m.reference} · {m.area_label} · {m.country} · {m.urgency}</div>
                    </div>
                    <Button size="sm" data-testid={`claim-${m.id}`} onClick={() => run(() => api.post(`/legal/matters/${m.id}/claim`), "Matter claimed")}>
                      Take it on
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="overline mb-2 text-slate-500">My client matters</div>
            {matters.assigned.length === 0 ? (
              <EmptyState testid="no-assigned" title="No client matters yet" description="Claim a matter and it moves here." />
            ) : (
              <div className="space-y-3">
                {matters.assigned.map((m) => (
                  <div key={m.id} data-testid={`assigned-${m.id}`} className="border border-border bg-white p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#0F172A]">{m.title}</div>
                        <div className="text-xs text-slate-500">{m.reference} · {m.client_name} · {m.area_label} · {m.country}</div>
                      </div>
                      <StatusBadge value={m.status} testid={`assigned-status-${m.id}`} />
                    </div>
                    <p className="mt-2 whitespace-pre-line text-xs text-slate-600">{m.description}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" data-testid={`ai-draft-${m.id}`} disabled={busy || m.status === "closed"} onClick={() => aiDraft(m)}>
                        <Sparkle size={14} className="mr-1" /> AI first pass
                      </Button>
                      <Button size="sm" data-testid={`reply-${m.id}`} disabled={m.status === "closed"} onClick={() => { setReply({ matter: m, body: "", next_steps: "", ai_assisted: false }); setDialog("reply"); }}>
                        Send advice
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`quote-btn-${m.id}`} disabled={m.status === "closed"} onClick={() => { setQuote({ matter: m, amount: "", currency: "usd", scope: "", turnaround_days: 5 }); setDialog("quote"); }}>
                        Fixed fee
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "guides" && (
        <div data-testid="guides-panel">
          {(isAdvisor || isAdmin) && (
            <div className="mb-4 flex justify-end">
              <Button data-testid="publish-guide-button" onClick={() => { setGuideForm(emptyGuide); setDialog("guide"); }}>
                <Plus size={16} className="mr-1.5" /> Publish a guide
              </Button>
            </div>
          )}
          {guides.length === 0 ? (
            <EmptyState testid="no-guides" title="No guides yet" description="Advocates publish plain-language guides on local law here." />
          ) : (
            <div className="grid gap-3 lg:grid-cols-2" data-testid="guide-list">
              {guides.map((g) => (
                <div key={g.id} data-testid={`guide-${g.id}`} className="border border-border bg-white p-4">
                  <div className="text-sm font-semibold text-[#0F172A]">{g.title}</div>
                  <div className="mt-1 text-xs text-slate-500">{areaLabel(g.area)} · {g.country} · {g.firm || g.author_name}</div>
                  <p className="mt-2 text-xs text-slate-600">{g.summary}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <a href={`/guides/${g.slug}`} target="_blank" rel="noreferrer" data-testid={`read-guide-${g.id}`} className="text-xs font-semibold text-[#002FA7] hover:underline">
                      Read the guide
                    </a>
                    {(g.author_id === user?.id || isAdmin) && (
                      <button type="button" data-testid={`delete-guide-${g.id}`} className="text-xs text-slate-500 hover:underline" onClick={() => run(() => api.delete(`/legal/guides/${g.id}`), "Guide removed")}>
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "verify" && isAdmin && (
        <div data-testid="verify-panel">
          {pending.length === 0 ? (
            <EmptyState testid="no-pending" title="Nothing to verify" description="New advocate registrations land here." />
          ) : (
            <div className="space-y-3">
              {pending.map((a) => (
                <div key={a.id} data-testid={`pending-${a.id}`} className="border border-border bg-white p-4">
                  <div className="text-sm font-semibold text-[#0F172A]">{a.firm}</div>
                  <div className="mt-1 text-xs text-slate-500">
                    {a.name} · {a.email} · {(a.countries || []).join(", ")} · licence {a.licence_no || "not given"} · {a.authority || "authority not given"}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" data-testid={`verify-${a.id}`} onClick={() => run(() => api.post(`/legal/advisors/${a.id}/verify`, { verified: true }), "Advocate verified")}>
                      Verify
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`reject-${a.id}`} onClick={() => run(() => api.post(`/legal/advisors/${a.id}/verify`, { verified: false, note: "Verification declined" }), "Left unverified")}>
                      Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Raise a matter */}
      <Dialog open={dialog === "matter"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Raise a legal matter</DialogTitle>
            <DialogDescription>A verified advocate admitted in that country picks it up, or the one you instructed.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>What is it about</Label><Input data-testid="matter-title-input" value={matterForm.title} onChange={(e) => setMatterForm({ ...matterForm, title: e.target.value })} placeholder="Apprentice contract terms" /></div>
            <div>
              <Label>Practice area</Label>
              <Select value={matterForm.area} onValueChange={(v) => setMatterForm({ ...matterForm, area: v })}>
                <SelectTrigger data-testid="matter-area-select"><SelectValue /></SelectTrigger>
                <SelectContent>{areas.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Country of law</Label><Input data-testid="matter-country-input" value={matterForm.country} onChange={(e) => setMatterForm({ ...matterForm, country: e.target.value.toUpperCase() })} /></div>
            <div>
              <Label>Urgency</Label>
              <Select value={matterForm.urgency} onValueChange={(v) => setMatterForm({ ...matterForm, urgency: v })}>
                <SelectTrigger data-testid="matter-urgency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{URGENCIES.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Relates to</Label>
              <Select value={matterForm.link_kind} onValueChange={(v) => setMatterForm({ ...matterForm, link_kind: v })}>
                <SelectTrigger data-testid="matter-link-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LINKS.map((l) => <SelectItem key={l} value={l}>{l.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {matterForm.link_kind !== "none" && (
              <div className="sm:col-span-2"><Label>Reference</Label><Input data-testid="matter-link-ref-input" value={matterForm.link_ref} onChange={(e) => setMatterForm({ ...matterForm, link_ref: e.target.value })} placeholder="PO-2026-0004, SHP-…, JOB-…" /></div>
            )}
            <div className="sm:col-span-2"><Label>Tell the advocate what happened</Label><Textarea data-testid="matter-description-input" rows={5} value={matterForm.description} onChange={(e) => setMatterForm({ ...matterForm, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button data-testid="submit-matter-button" disabled={busy || !matterForm.title} onClick={raiseMatter}>Raise matter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Advice reply */}
      <Dialog open={dialog === "reply"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Send advice</DialogTitle>
            <DialogDescription>
              {reply.ai_assisted
                ? "This started as an AI first pass — edit it and confirm before it reaches the client."
                : reply.matter?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div><Label>Advice</Label><Textarea data-testid="reply-body-input" rows={10} value={reply.body} onChange={(e) => setReply({ ...reply, body: e.target.value })} /></div>
            <div><Label>Next steps</Label><Input data-testid="reply-next-input" value={reply.next_steps} onChange={(e) => setReply({ ...reply, next_steps: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button data-testid="submit-reply-button" disabled={busy || reply.body.trim().length < 20} onClick={sendReply}>Confirm &amp; send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fixed fee */}
      <Dialog open={dialog === "quote"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Quote a fixed fee</DialogTitle>
            <DialogDescription>{quote.matter?.reference}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Amount</Label><Input data-testid="quote-amount-input" type="number" value={quote.amount} onChange={(e) => setQuote({ ...quote, amount: e.target.value })} /></div>
            <div><Label>Currency</Label><Input data-testid="quote-currency-input" value={quote.currency} onChange={(e) => setQuote({ ...quote, currency: e.target.value.toLowerCase() })} /></div>
            <div><Label>Turnaround (days)</Label><Input data-testid="quote-days-input" type="number" value={quote.turnaround_days} onChange={(e) => setQuote({ ...quote, turnaround_days: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>What the fee covers</Label><Textarea data-testid="quote-scope-input" rows={3} value={quote.scope} onChange={(e) => setQuote({ ...quote, scope: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button data-testid="submit-quote-button" disabled={busy || !quote.amount || !quote.scope} onClick={sendQuote}>Send fee</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Practice profile */}
      <Dialog open={dialog === "practice"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>My practice</DialogTitle>
            <DialogDescription>An admin verifies your licence before you appear in the directory.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Practice / firm name</Label><Input data-testid="practice-firm-input" value={profileDraft.firm} onChange={(e) => setProfileDraft({ ...profileDraft, firm: e.target.value })} /></div>
            <div><Label>Countries admitted (comma separated)</Label><Input data-testid="practice-countries-input" value={profileDraft.countries} onChange={(e) => setProfileDraft({ ...profileDraft, countries: e.target.value.toUpperCase() })} placeholder="US, IN, KE" /></div>
            <div><Label>City</Label><Input data-testid="practice-city-input" value={profileDraft.city || ""} onChange={(e) => setProfileDraft({ ...profileDraft, city: e.target.value })} /></div>
            <div><Label>Licence / roll number</Label><Input data-testid="practice-licence-input" value={profileDraft.licence_no || ""} onChange={(e) => setProfileDraft({ ...profileDraft, licence_no: e.target.value })} /></div>
            <div><Label>Admitting authority</Label><Input data-testid="practice-authority-input" value={profileDraft.authority || ""} onChange={(e) => setProfileDraft({ ...profileDraft, authority: e.target.value })} placeholder="Bar Council, Law Society…" /></div>
            <div><Label>Years qualified</Label><Input data-testid="practice-years-input" type="number" value={profileDraft.years_qualified} onChange={(e) => setProfileDraft({ ...profileDraft, years_qualified: e.target.value })} /></div>
            <div><Label>Hourly rate</Label><Input data-testid="practice-rate-input" type="number" value={profileDraft.hourly_rate} onChange={(e) => setProfileDraft({ ...profileDraft, hourly_rate: e.target.value })} /></div>
            <div><Label>Currency</Label><Input data-testid="practice-currency-input" value={profileDraft.currency} onChange={(e) => setProfileDraft({ ...profileDraft, currency: e.target.value.toLowerCase() })} /></div>
            <div><Label>Languages</Label><Input data-testid="practice-languages-input" value={profileDraft.languages} onChange={(e) => setProfileDraft({ ...profileDraft, languages: e.target.value })} placeholder="English, Hindi" /></div>
            <div className="sm:col-span-2">
              <Label>Practice areas</Label>
              <div className="mt-1.5 flex flex-wrap gap-2" data-testid="practice-areas">
                {areas.map((a) => {
                  const on = (profileDraft.practice_areas || []).includes(a.key);
                  return (
                    <button
                      key={a.key}
                      type="button"
                      data-testid={`practice-area-${a.key}`}
                      onClick={() => setProfileDraft({
                        ...profileDraft,
                        practice_areas: on
                          ? profileDraft.practice_areas.filter((k) => k !== a.key)
                          : [...(profileDraft.practice_areas || []), a.key],
                      })}
                      className={`border px-2 py-1 text-[0.65rem] uppercase tracking-wider transition-colors duration-150 ${on ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-border text-slate-600 hover:bg-slate-50"}`}
                    >
                      {a.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="sm:col-span-2"><Label>About the practice</Label><Textarea data-testid="practice-bio-input" rows={4} value={profileDraft.bio || ""} onChange={(e) => setProfileDraft({ ...profileDraft, bio: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button data-testid="save-practice-button" disabled={busy || !profileDraft.firm} onClick={saveProfile}>Save practice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guide */}
      <Dialog open={dialog === "guide"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Publish a plain-language guide</DialogTitle>
            <DialogDescription>Anyone signed in can read it, and it gets a public link.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Title</Label><Input data-testid="guide-title-input" value={guideForm.title} onChange={(e) => setGuideForm({ ...guideForm, title: e.target.value })} placeholder="What an apprenticeship contract must say" /></div>
            <div>
              <Label>Area</Label>
              <Select value={guideForm.area} onValueChange={(v) => setGuideForm({ ...guideForm, area: v })}>
                <SelectTrigger data-testid="guide-area-select"><SelectValue /></SelectTrigger>
                <SelectContent>{areas.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Country</Label><Input data-testid="guide-country-input" value={guideForm.country} onChange={(e) => setGuideForm({ ...guideForm, country: e.target.value.toUpperCase() })} /></div>
            <div className="sm:col-span-2"><Label>One-line summary</Label><Input data-testid="guide-summary-input" value={guideForm.summary} onChange={(e) => setGuideForm({ ...guideForm, summary: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>The guide</Label><Textarea data-testid="guide-body-input" rows={8} value={guideForm.body} onChange={(e) => setGuideForm({ ...guideForm, body: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button data-testid="submit-guide-button" disabled={busy || !guideForm.title} onClick={publishGuide}>Publish</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
